import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { GeneratedImage, GenerationResult, StylePreset } from '@gilding/shared';
import { api } from '../api/client.js';
import { cn } from '../lib/classnames.js';

interface FixDialogProps {
  parent: GenerationResult;
  image: GeneratedImage;
  open: boolean;
  onClose: () => void;
  onSuccess: (next: GenerationResult) => void;
}

/**
 * Fix suggestions are CONTEXTUAL to the image being refined — not a fixed list.
 * They change with (a) whether a Whacky Whale character was used (only then do
 * the whale-anatomy fixes like the single-fin correction make sense) and (b)
 * the style preset, since "fix the text" matters for a poster but not a
 * photoreal portrait. We surface the most-likely-needed handful so a tap
 * usually beats typing, without ever suggesting a whale fix on a non-whale image.
 */
const STYLE_FIXES: Record<StylePreset, string[]> = {
  'photoreal-cinematic': ['Sharpen the focus', 'Warm the lighting', 'Deepen the background blur'],
  'editorial-illustration': ['Clean up the linework', 'Even out the colours', 'Simplify the background'],
  'anime-leaning': ['Sharpen the line art', 'Brighten the eyes', 'Add more contrast'],
  '3d-render': ['Smooth the surfaces', 'Fix the reflections', 'Soften the shadows'],
  painterly: ['Soften the brushwork', 'Richen the palette', 'Add more depth'],
  'logo-poster': ['Fix the text spelling', 'Straighten the type', 'Increase the contrast'],
  experimental: ['Push the colours further', 'Add more texture', 'Heighten the mood'],
};

const UNIVERSAL_FIXES = ['Brighten the background', 'Tighten the framing', 'Clean up the edges'];

function buildFixSuggestions(parent: GenerationResult): string[] {
  const usedCharacter =
    parent.request.characterTokenId !== null || parent.request.referenceImageUrl !== null;
  const out: string[] = [];
  // Whale-only fixes lead when a character was attached — these are the edits
  // a whale gilding actually needs (and the reason the old hardcoded
  // "duplicate tail fin" suggestion was wrong for everyone else).
  if (usedCharacter) {
    out.push('Make the hands a single fin unit', 'Remove any extra fins');
  }
  const styleFixes = STYLE_FIXES[parent.request.stylePreset] ?? [];
  for (const s of [...styleFixes, ...UNIVERSAL_FIXES]) {
    if (out.length >= 5) break;
    if (!out.includes(s)) out.push(s);
  }
  return out.slice(0, 5);
}

/**
 * FixDialog — surgical-edit modal for a single variant.
 *
 * Visual vocabulary follows Result.tsx + NewGeneration.tsx: no left rails,
 * no opacity/backdrop-blur surfaces, no rounded-full pills, no uppercase
 * mini-cap labels, no unicode glyphs masquerading as icons. The textarea
 * uses the `.field` baseline-rule treatment; suggestions are `.tag` chips,
 * not pills; the close glyph is a bespoke SVG; the primary action is
 * `.btn-primary`. The dialog traps focus and restores it on close.
 */
interface FixReference {
  url: string;
  name: string;
}

const MAX_REFERENCES = 4;

export function FixDialog({ parent, image, open, onClose, onSuccess }: FixDialogProps) {
  const [instruction, setInstruction] = useState('');
  const [references, setReferences] = useState<FixReference[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const textareaId = useId();
  const helperId = useId();
  const counterId = useId();
  const titleId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      setInstruction('');
      setReferences([]);
      setUploadError(null);
      setUploadingCount(0);
    }
  }, [open]);

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    const remaining = MAX_REFERENCES - references.length;
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length < files.length) {
      setUploadError(`Up to ${MAX_REFERENCES} references per fix.`);
    }
    setUploadingCount((c) => c + toUpload.length);
    for (const file of toUpload) {
      try {
        const { url } = await api.uploadReference(file);
        setReferences((prev) => [...prev, { url, name: file.name }]);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setUploadError(message);
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const removeReference = (idx: number): void => {
    setReferences((prev) => prev.filter((_, i) => i !== idx));
  };

  // Capture the previously-focused element so we can restore it on close.
  // Move focus into the dialog on open.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;
    // Defer focus to next frame so the panel is mounted + visible.
    const id = window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(id);
      // Restore focus to whatever opened the dialog.
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Close on Escape + focus trap on Tab.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const suggestions = buildFixSuggestions(parent);

  const fixStatusQuery = useQuery({
    queryKey: ['fix-status', parent.id],
    queryFn: () => api.fixStatus(parent.id),
    enabled: open,
    staleTime: 0,
  });

  const fixMut = useMutation({
    mutationFn: () =>
      api.generate({
        sketch: '',
        stylePreset: parent.request.stylePreset,
        aspectRatio: parent.request.aspectRatio,
        mode: 'single',
        modelOverride: null,
        referenceImageUrl: null,
        hasReference: false,
        remixSourceId: null,
        fixSourceImageUrl: image.url,
        fixInstruction: instruction.trim(),
        fixReferenceImageUrls: references.map((r) => r.url),
        characterTokenId: null,
      }),
    // No auto-close on success. Show the result inline with an explicit
    // "View edited result" button — the user knows the fix landed and can
    // choose to navigate. Closes the silent-success path.
  });

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const fixStatus = fixStatusQuery.data;
  const cost = fixStatus?.fixCreditCost ?? 1;
  const isFree = cost === 0;
  const disabled = instruction.trim().length < 4 || fixMut.isPending;
  // Handle every error shape — generic Error, plain object with .message,
  // a string, or an empty error (we still want to show SOMETHING). Also
  // capture .detail (the worker attaches per-variant failures here).
  const errInfo = (() => {
    const e = fixMut.error as unknown;
    if (!e) return null;
    let message = 'Fix failed.';
    let code: string | null = null;
    let detail: unknown = null;
    if (e instanceof Error) {
      message = e.message || message;
      const ext = e as Error & { code?: string; detail?: unknown };
      code = ext.code ?? null;
      detail = ext.detail ?? null;
    } else if (typeof e === 'string') {
      message = e;
    } else if (e && typeof e === 'object') {
      const obj = e as { message?: unknown; code?: unknown; detail?: unknown };
      if (typeof obj.message === 'string') message = obj.message;
      if (typeof obj.code === 'string') code = obj.code;
      if (obj.detail) detail = obj.detail;
    }

    // Detect the specific "source image expired" failure mode. The error
    // shape from the worker is { failures: [{ reason: 'openai_edit_fetch_ref_404' }] }.
    // These are legacy images generated before image persistence shipped — the
    // upstream URL has expired so we can't re-edit them. Explain it clearly
    // so the user knows the path forward (regenerate to make the new images
    // permanently fixable).
    let humanMessage = message;
    let isExpiredSource = false;
    if (detail && typeof detail === 'object' && 'failures' in detail) {
      const failures = (detail as { failures?: { reason?: string }[] }).failures;
      if (Array.isArray(failures) && failures.some((f) => /fetch_ref_404/.test(f.reason ?? ''))) {
        isExpiredSource = true;
        humanMessage =
          'This image came from an earlier batch whose source bytes have since expired. Fix mode only works on images generated recently. Regenerate the scene to enable Fix on the new variants.';
      }
    }

    return { message: humanMessage, code, detail, isExpiredSource };
  })();
  const error = errInfo?.message ?? null;
  const errorDetail = errInfo?.detail ?? null;
  const isExpiredSource = errInfo?.isExpiredSource ?? false;
  const result = fixMut.data;
  const successButNoId = !!result && !result.id;

  // Render through a portal on document.body. The dialog is position:fixed,
  // and any ancestor with a transform/filter (e.g. the page-enter transition
  // wrapper, or a .reveal block) would otherwise become its containing block —
  // pushing the bottom-sheet off-screen. The portal escapes all of that.
  return createPortal(
    <div
      className="backdrop-enter fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 md:items-center md:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={panelRef}
        className={cn(
          'sheet-enter flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-y-auto',
          'rounded-t-md bg-surface-elevated p-6 md:rounded-md',
        )}
        style={{ boxShadow: 'var(--shadow-lift)', border: '1px solid rgb(var(--color-hairline-strong))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-display italic text-caption text-ink-subtle">
              Refine one image
            </span>
            <h2 id={titleId} className="heading-section">
              What needs fixing?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-ink-subtle transition-colors duration-150 hover:text-ink"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-5 flex items-start gap-4">
          <div className="card-plate flex h-24 w-24 flex-shrink-0 self-start items-center justify-center overflow-hidden">
            <img
              src={image.url}
              alt=""
              className="block h-full w-full rounded-xs object-cover"
            />
          </div>
          <div className="flex flex-col gap-1 text-body text-ink-soft">
            <p>
              Describe the single change to make. Claude will write a surgical edit
              instruction; the rest of the image stays as-is.
            </p>
            <p className="font-display italic text-caption text-ink-subtle">
              Variant {image.idx + 1} · {parent.request.stylePreset}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <label
            htmlFor={textareaId}
            className="font-display italic text-caption text-ink-subtle"
          >
            The fix
          </label>
          <textarea
            id={textareaId}
            ref={textareaRef}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Brighten the background a touch and clean up the edges, keeping everything else as it is."
            className="field font-display text-lede"
            aria-describedby={`${helperId} ${counterId}`}
          />
          <p
            id={counterId}
            className="text-right font-display text-caption text-ink-subtle"
            aria-live="polite"
          >
            {instruction.length} / 500
          </p>
        </div>

        <p id={helperId} className="sr-only">
          Describe the single change you want; Claude will preserve the rest of the
          image.
        </p>

        <div
          role="group"
          aria-label="Suggested fixes"
          className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-2"
        >
          <span className="font-display italic text-caption text-ink-subtle">
            Try:
          </span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setInstruction(s)}
              className="tag font-display italic text-caption text-ink-soft transition-colors duration-150 hover:text-ink"
            >
              <span className="tag-dot" />
              {s}
            </button>
          ))}
        </div>

        {/* ---- Reference image uploads ---- */}
        <div className="mt-6 flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-display italic text-caption text-ink-subtle">
              Attach a reference (optional)
            </span>
            <span className="font-display italic text-caption text-ink-subtle">
              {references.length} / {MAX_REFERENCES}
            </span>
          </div>
          <p className="text-caption text-ink-soft">
            Upload a logo, portrait, or texture to composite into the fix. Claude sees it
            too. The prompt will describe exactly what to keep from your reference.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {references.map((ref, idx) => (
              <div
                key={ref.url}
                className="group relative h-20 w-20 overflow-hidden rounded-xs border border-hairline-strong bg-surface-sunken"
                title={ref.name}
              >
                <img
                  src={ref.url}
                  alt={ref.name}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeReference(idx)}
                  className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-hairline-strong bg-surface/95 text-ink-subtle opacity-0 transition-opacity duration-150 hover:text-ink group-hover:opacity-100"
                  aria-label={`Remove reference ${idx + 1}`}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                    <path d="M2 2 L8 8 M8 2 L2 8" />
                  </svg>
                </button>
              </div>
            ))}
            {uploadingCount > 0 &&
              Array.from({ length: uploadingCount }).map((_, i) => (
                <div
                  key={`upload-${i}`}
                  className="sweep h-20 w-20 rounded-xs border border-hairline/60 bg-surface-sunken"
                  aria-label="Uploading…"
                />
              ))}
            {references.length + uploadingCount < MAX_REFERENCES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xs border border-dashed border-hairline-strong bg-transparent text-ink-subtle transition-colors duration-150 hover:border-accent hover:text-ink"
                aria-label="Attach reference image"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 5 L12 19 M5 12 L19 12" />
                </svg>
                <span className="font-display text-[10px] uppercase tracking-widest">Attach</span>
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            className="sr-only"
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {uploadError && (
            <p className="font-display italic text-caption text-danger">{uploadError}</p>
          )}
        </div>

        {error && (
          <div
            className={cn(
              'mt-4 rounded-sm border p-3',
              isExpiredSource
                ? 'border-hairline-strong bg-surface-sunken'
                : 'border-danger/30 bg-danger/5',
            )}
            role="alert"
          >
            <p
              className={cn(
                'font-display text-body',
                isExpiredSource ? 'text-ink' : 'text-caption text-danger',
              )}
            >
              {error}
            </p>
            {!isExpiredSource && errorDetail !== null && (
              <details className="mt-2">
                <summary className="cursor-pointer font-display italic text-caption text-ink-subtle">
                  Show detail
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xs border border-hairline/60 bg-surface-sunken p-2 font-mono text-[11px] leading-snug text-ink-subtle">
                  {(() => {
                    try {
                      return JSON.stringify(errorDetail, null, 2);
                    } catch {
                      // Circular or non-serialisable — show what we can.
                      return Object.prototype.toString.call(errorDetail);
                    }
                  })()}
                </pre>
              </details>
            )}
          </div>
        )}

        {successButNoId && (
          <div
            className="mt-4 rounded-sm border border-danger/30 bg-danger/5 p-3"
            role="alert"
          >
            <p className="font-display text-caption text-danger">
              The fix came back without a result id. Something went wrong server-side.
              Try again, or contact support if it persists.
            </p>
          </div>
        )}

        {result && result.id && (
          <div className="mt-5 rounded-sm border border-accent/40 bg-accent/5 p-4">
            <p className="font-display italic text-caption text-ink-subtle">The fix is done</p>

            {/* Before → after: the original variant beside the edited one, so the
                change reads at a glance. The "after" wears the gilded ring. */}
            <div className="mt-4 flex items-center justify-center gap-3 sm:gap-5">
              <figure className="flex flex-col items-center gap-2">
                <div className="card-plate">
                  <img src={image.url} alt="Before the fix" className="block h-24 w-24 rounded-xs object-cover sm:h-28 sm:w-28" />
                </div>
                <figcaption className="font-display italic text-caption text-ink-subtle">before</figcaption>
              </figure>

              <FixArrow />

              <figure className="flex flex-col items-center gap-2">
                <div className="card-plate ring-2 ring-inset ring-accent">
                  {result.images?.[0]?.url && (
                    <img src={result.images[0].url} alt="After the fix" className="block h-24 w-24 rounded-xs object-cover sm:h-28 sm:w-28" />
                  )}
                </div>
                <figcaption className="font-display text-caption text-accent-deep">after</figcaption>
              </figure>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="font-display italic text-caption text-ink-subtle">
                {result.creditsDebited === 0
                  ? 'Free fix'
                  : `${result.creditsDebited} ${result.creditsDebited === 1 ? 'leaf' : 'leaves'}`}{' '}
                · kept in your gallery
              </p>
              <button type="button" className="btn-primary" onClick={() => onSuccess(result)}>
                View edited result
              </button>
            </div>
          </div>
        )}

        <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
          <p className="font-display italic text-caption text-ink-subtle">
            {fixStatusQuery.isLoading
              ? 'Checking your free fix…'
              : fixStatus?.unlimitedFixes
                ? 'Fixes are free on this account.'
                : isFree
                  ? 'First fix is free for this set.'
                  : '1 leaf (free fix already used).'}
          </p>
          <div className="flex items-center gap-3">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {result && result.id ? 'Close' : 'Cancel'}
            </button>
            {!(result && result.id) && (
              <button
                type="button"
                className="btn-primary"
                disabled={disabled}
                onClick={() => fixMut.mutate()}
              >
                {fixMut.isPending
                  ? 'Fixing…'
                  : isFree
                    ? 'Apply fix (free)'
                    : 'Apply fix (1 leaf)'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** A small gilded arrow marking the before → after transition. */
function FixArrow() {
  return (
    <svg
      width="26"
      height="16"
      viewBox="0 0 26 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-accent"
    >
      <path d="M2 8 H21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M16 3 L22 8 L16 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4 L12 12" />
      <path d="M12 4 L4 12" />
    </svg>
  );
}
